import { type ComponentProps } from 'react'

import { clsx } from 'clsx'

import type { IconComponentType } from '../Icon/types.js'
import {
  avatarIconStyles,
  avatarSizeInPixel,
  avatarStyles,
} from './Avatar.styles.js'
import { AvatarFallback, AvatarFallbackProps } from './AvatarFallback.js'
import { AvatarRounded, AvatarSize } from './types.js'

export type AvatarProps = ComponentProps<'div'> & {
  size?: AvatarSize
  src?: string | null
  name?: string
  icon?: IconComponentType
  rounded?: AvatarRounded
  fallback?: (props: AvatarFallbackProps) => JSX.Element
}

/**
 * The Avatar component is used to represent a user, and displays the profile picture, initials or fallback icon.
 */
export const Avatar = ({
  size = 'md',
  rounded = 'avatar',
  src,
  name,
  icon,
  className,
  fallback,
  ...rest
}: AvatarProps) => {
  if (!src) {
    const Component = fallback || AvatarFallback
    return (
      <div
        className={clsx(
          'relative',
          avatarStyles({
            rounded,
            size,
          }),
          className,
        )}
        title={name}
      >
        <Component size={size} name={name} {...rest} />
        {icon && <div className={avatarIconStyles({ size })}>{icon}</div>}
      </div>
    )
  }

  return (
    <div
      className={clsx(
        'relative',
        avatarStyles({
          rounded,
          size,
        }),
        className,
      )}
      title={name}
      {...rest}
    >
      <img
        className={clsx(
          avatarStyles({
            rounded,
            size,
          }),
          'object-cover object-center',
        )}
        height={avatarSizeInPixel[size] || 40}
        width={avatarSizeInPixel[size] || 40}
        src={src}
        alt={name}
      />
      {icon && <div className={avatarIconStyles({ size })}>{icon}</div>}
    </div>
  )
}
